import { ButtonScaleViewOpts } from 'src/lib/ui/components/ButtonScaleView';
import { FeatureButton } from 'src/game/components/FeatureButton';
import { AssetGroup } from '@play-co/timestep-core';
import { spineLoader } from '@play-co/timestep-spine';
import { now, user } from 'src/StateObserver';
import {
  getOngoingWeeklyLeaderboardEvent,
  isWeeklyLeaderboardsAvailable,
  isWeeklyLeaderboardsUnlocked,
} from 'src/replicant/weeklyLeaderboard/weeklyLeaderboard.getters';
import { downscale } from 'src/conf/scale';
import { ImageScaleView, View } from '@play-co/timestep-core/ui';
import i18n from 'src/lib/i18n/i18n';
import {
  getWeeklyLeaderboardEventToClaim,
  getWeeklyLeaderboardRanking,
} from '../weeklyLeaderboard.utils';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import { animateJumpingPoints } from 'src/game/animation/jumpingPoints';
import { weeklyLeaderboardStarScoreMap } from 'src/replicant/weeklyLeaderboard/weeklyLeaderboard.ruleset';
import { isPlatformGuest } from 'src/lib/utils';
import { GCInstant } from 'src/lib/gcinstant';
import { isGuestUser } from 'src/replicant/guest/guest.getters';

const assets = {
  spine: {
    loadMethod: spineLoader,
    url: 'assets/ui/weeklyLeaderboard/animation/weekly-leaderboard-button',
  },
  claimBg: 'assets/ui/weeklyLeaderboard/button/claim-bg.png',
  goldenMug: 'assets/ui/weeklyLeaderboard/button/golden-mug.png',
  rankBg: 'assets/ui/weeklyLeaderboard/button/rank-bg.png',
};

export class WeeklyLeaderboardButton extends FeatureButton {
  private rankBackground: ImageScaleView;
  private readonly rankLabel: LangBitmapFontTextView;

  public override getElementsToApplyFilter() {
    const elements: View[] = [];
    if (this.labelPanel) elements.push(this.labelPanel);
    if (this.spine) elements.push(this.spine);
    return elements;
  }

  constructor(opts: ButtonScaleViewOpts) {
    super({
      width: 144,
      height: 165,
      pressedOffsetY: 5,
      labelPanel: {
        width: downscale(195),
        height: downscale(55),
        offsetX: 2,
        offsetY: -4,
        labelOffsetX: -1,
        background: {
          image: assets.claimBg,
        },
      },
      ...opts,
    });

    this.animationStateMap = {
      main: {
        type: 'spine',
        assetGroup: new AssetGroup(Object.values(assets)),
        url: assets.spine.url,
        scale: 0.33,
        offsetX: 1,
        offsetY: 60,
        sequence: ['idle'],
      },
    };

    this.rankBackground = new ImageScaleView({
      superview: this.container,
      image: assets.rankBg,
      centerOnOrigin: true,
      x: this.container.style.width / 2 + 3,
      y: 115,
      width: downscale(147),
      height: downscale(48),
      visible: false,
    });

    this.rankLabel = new LangBitmapFontTextView({
      superview: this.rankBackground,
      align: 'center',
      verticalAlign: 'center',
      size: 20,
      offsetY: -3,
      font: 'Title',
      width: this.rankBackground.style.width,
      height: this.rankBackground.style.height,
      color: '#FFFFFF',
    });
    this.rankLabel.localeText = () => '';

    this.setNotification({
      condition: (user, now) => {
        // Do not show if button is locked
        if (this.isLocked) return false;

        // Have an event rewards to claim
        if (getWeeklyLeaderboardEventToClaim()) return true;

        // Check if the event is available
        const event = getOngoingWeeklyLeaderboardEvent(user, now);
        if (!event) return false;

        // If the event is started, check if the user has joined
        if (user.weeklyLeaderboard?.entries[event.id]) {
          return false; // Already joined
        }

        // If the event is not started yet, show the badge
        return true;
      },
    });

    // Start updating rank
    this.updateRank();
  }

  private async updateRank(eventId?: string) {
    // If no event id is provided, get the ongoing event
    if (!eventId) {
      const event = getOngoingWeeklyLeaderboardEvent(user(), now());
      if (!event) {
        this.rankBackground.updateOpts({ visible: false });
        return;
      }
      eventId = event.id;
    }

    // Find if user has joined the event and has opponents
    const userEntry = user().weeklyLeaderboard?.entries[eventId];
    if (!userEntry || !userEntry.opponents.length) {
      this.rankBackground.updateOpts({ visible: false });
      return;
    }

    // Get the cached ranking
    let ranking = getWeeklyLeaderboardRanking(eventId)?.getCached();

    // If no cached ranking, try to fetch it. This runs every second, so a failure only hides the
    // rank: `useCache` holds it for a cooldown before the next attempt.
    if (!ranking) {
      ranking = await getWeeklyLeaderboardRanking(eventId)
        ?.get()
        .catch(() => undefined);
    }

    // If the ranking is still not available, hide the rank
    if (!ranking) {
      this.rankBackground.updateOpts({ visible: false });
      return;
    }

    // Get user's position
    const userRank = ranking.playerPosition;

    // If no position, hide the rank
    if (!userRank) {
      this.rankBackground.updateOpts({ visible: false });
      return;
    }

    // Update the rank with the user's position
    this.rankLabel.localeText = () =>
      i18n('match2.tournament.position', {
        position: userRank ? userRank.toString() : '?',
      });
    this.rankBackground.updateOpts({ visible: true });
  }

  public async playProgressAnimation(stars = 3) {
    await animateJumpingPoints({
      button: this,
      count: weeklyLeaderboardStarScoreMap[stars],
      iconImage: assets.goldenMug,
    });
  }

  /**
   * Determines whether the weekly leaderboard button should be visible to the user.
   *
   * The button is visible in the following cases:
   * - weekly leaderboards are unlocked for the user.
   * - If weekly leaderboards are available for the user at the current time.
   *
   * @returns {boolean} `true` if the button should be visible, otherwise `false`.
   */
  private isButtonVisible() {
    // Show on fake guest weekly leaderboard
    if (
      isPlatformGuest(GCInstant) &&
      isGuestUser(user()) &&
      isWeeklyLeaderboardsUnlocked(user())
    ) {
      return true;
    }

    // Normal case: show if the weekly leaderboards are available
    if (isWeeklyLeaderboardsAvailable(user(), now())) {
      return true;
    }

    // default case: hide the button
    return false;
  }

  public update() {
    // Hide the button if the feature is not available for the user
    if (!this.isButtonVisible()) {
      this.hide();
      return;
    }

    this.show();
    this.setAnimationState('main');

    const eventToClaim = getWeeklyLeaderboardEventToClaim();

    if (eventToClaim) {
      this.setLabelLocaleText('weeklyLeaderboard.finished');
    } else {
      const event = getOngoingWeeklyLeaderboardEvent(user(), now());
      const remainingTime = event?.remainingTime ?? 0;
      this.setLabelTime({ duration: remainingTime });
    }

    this.updateLabelPanel({
      background: { visible: !!eventToClaim },
    });
    this.updateRank(eventToClaim);
  }
}
